import { ChangeDetectorRef, Component } from '@angular/core';

import { Store } from '@ngrx/store';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ActivitiesListComponent } from '@recruitee/activities-and-notifications';
import {
  ActivitiesDataScope,
  DefaultActivitiesAndNotificationsFilters,
  getStoreScope,
  TrackingActivitiesActions,
} from '@recruitee/activities-and-notifications-data';
import type { TrackingActivity } from '@recruitee/activities-types';
import { filterBoolean } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import {
  EmptyStateComponent,
  ScrollableDirective,
  SpinnerComponent,
} from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

import { ActivityPageDataService } from '../../../data/single-requisition/activity-page-data.service';
import { ActivityFacade } from '../../../facades/single-requisition/activity.facade';

@Component({
  selector: 'rt-requisitions-activities-container',
  templateUrl: './requisitions-activities-container.component.html',
  styleUrls: ['./requisitions-activities-container.component.less'],
  imports: [
    EmptyStateComponent,
    InfiniteScrollModule,
    SpinnerComponent,
    ActivitiesListComponent,
    TranslatePipe,
  ],
  standalone: true,
})
export class RequisitionsActivitiesContainerComponent {
  public isLoaded: boolean;
  public activities: TrackingActivity[] = [];
  public canManageCandidate: boolean;

  private requisitionId: number;
  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;
  private filters = { ...DefaultActivitiesAndNotificationsFilters };

  constructor(
    public scrollable: ScrollableDirective,
    private data: ActivityPageDataService,
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private activityFacade: ActivityFacade,
  ) {}

  public ngOnInit() {
    combineLatest([
      this.data.activities$,
      this.data.manageCandidateAbility$,
      this.data.requisition$,
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([activities, canManageCandidate, requisition]) => {
        this.activities = activities;
        this.canManageCandidate = canManageCandidate;
        this.requisitionId = requisition.id;
        this.cdRef.detectChanges();
      });

    this.data.isLoaded$.pipe(filterBoolean, takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.isLoaded = true;
      this.cdRef.detectChanges();
    });
  }

  public openActivity(activity: TrackingActivity): void {
    this.activityFacade.openActivity(activity);
  }

  public loadMore(): void {
    this.filters = {
      ...this.filters,
      page: this.filters.page! + 1,
    };
    this.loadActivities();
  }

  private loadActivities(): void {
    const requisitionActivitiesScope = getStoreScope(
      ActivitiesDataScope.Requisition,
      this.requisitionId,
    );
    this.store.dispatch(
      TrackingActivitiesActions.fetchRequisitionTrackingActivities({
        payload: {
          params: {
            requisitionId: this.requisitionId,
            filters: this.filters,
          },
          scope: requisitionActivitiesScope,
          replace: false,
        },
      }),
    );
  }
}
